import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ChevronDown, ChevronUp } from 'lucide-react';
import { siteData } from '../data/mock';

const Avis = () => {
  const [expandedReviews, setExpandedReviews] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const toggleReview = (id) => {
    setExpandedReviews((prev) =>
      prev.includes(id) ? prev.filter((reviewId) => reviewId !== id) : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pt-20">
      {/* Hero */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max text-center">
          <div className="animated">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Témoignages Clients
            </h1>
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={32} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-3xl font-bold">{siteData.business.rating}/5</span>
            </div>
            <p className="text-xl text-gray-300">
              Basé sur {siteData.business.reviewCount} avis vérifiés
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Ce que disent mes clients
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-2xl mx-auto">
              Des transformations réelles, des résultats durables, des vies changées
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {siteData.testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="network-card animated"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <span className="text-sm text-[var(--text-light)]">{testimonial.date}</span>
                </div>
                <p className="text-[var(--text-secondary)] mb-4 text-lg italic">
                  "{testimonial.text}"
                </p>
                <p className="font-semibold text-[var(--text-primary)] text-lg">
                  {testimonial.name}
                </p>
              </div>
            ))}
          </div>

          {/* Highlights */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              {
                title: "59 avis",
                description: "Clients satisfaits qui partagent leur expérience"
              },
              {
                title: "5.0/5",
                description: "Note moyenne parfaite sur l'ensemble des avis"
              },
              {
                title: "100% recommandé",
                description: "Tous mes clients recommandent mes services"
              }
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center animated"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-5xl font-bold text-[var(--brand-primary)] mb-2">
                  {stat.title}
                </div>
                <p className="text-[var(--text-secondary)] text-lg">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Questions fréquentes
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {siteData.faqs.map((faq, index) => (
              <div
                key={index}
                className="network-card animated"
                style={{ transitionDelay: `${index * 30}ms` }}
              >
                <button
                  onClick={() => toggleReview(index)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="text-xl font-semibold text-[var(--text-primary)] pr-4">
                    {faq.question}
                  </h3>
                  {expandedReviews.includes(index) ? (
                    <ChevronUp className="text-[var(--brand-primary)] flex-shrink-0" size={24} />
                  ) : (
                    <ChevronDown className="text-[var(--brand-primary)] flex-shrink-0" size={24} />
                  )}
                </button>
                {expandedReviews.includes(index) && (
                  <p className="mt-4 text-[var(--text-secondary)] text-lg">
                    {faq.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max text-center">
          <div className="max-w-3xl mx-auto animated">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Rejoins les clients satisfaits
            </h2>
            <p className="text-xl mb-8 text-gray-300">
              Commence ton parcours de transformation dès aujourd'hui
            </p>
            <Link to="/contact" className="btn-primary">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Avis;
